import { observable, computed, toJS, action, transaction } from 'mobx'
import { formatMessage } from '../translations'
import { specialPageIds, PAGE_ID_STARTPAGE } from '../shared/const'
import Model from '../shared/model'
import { i18n, getUserNameById } from '../shared/utils'
import { lockable } from '../shared/decorators/communicator'
import { deepGet } from '../shared/obj'
import { CircularDependencyFreeStore } from '../CircularDependencyFreeStore'
import {
  InsertIntoPageCommand,
  RemoveFromPageCommand,
  MoveInPageCommand,
  UpdatePageContentCommand,
} from './commands'
import GridBlock from './GridBlock'

/**
 * Properties:
    autofilled : 0
    changedAt : "2022-11-21 15:28:32"
    content : "<div></div>"
    createFolder : false
    createdAt : "2021-09-13 12:00:20"
    createdBy : "5902018aedffcb32008b4567"
    createdIso : "en"
    editable : 1
    hasChanged : 1
    header : HTML Node for the header
    hideBreadcrumb : 0
    hideNavigation : 0
    i18n : {,…}
    id : 16241
    internalLinks: {data:[{ count: number, id: number }]}
    isGhostPage : 0
    isSilent : false
    itemTemplates : {article: {4: {id: 4, vendor: "default", shortcut: "hotspot"},…},…}
    items : [{id: 106190, type: "article", templateId: 22, data: {,…},…},…]
    lastPublishedAt : null
    lastUnpublishedAt : null
    meta : {censhareArticleIds: [495758]}
    navigationArticleId : null
    parentId : 34
    position : 2
    projectId : 3
    publicationNotAllowed : 0
    publishAt : null
    publishMailTo : null
    publishedUrl : null
    realUrl : null
    redirectTarget : 1
    redirectType : 1
    redirectUrl : ""
    releasePublicationLockAt : null
    sourceId : null
    status : null
    template : {vendor: "atrivio", shortcut: "main-page"}
    templateData : {,…}
    templateId : 2
    type : 1
    unpublishAt : null
    unpublishMailTo : null
    updatedAt : "2022-11-21 15:28:32"
    updatedBy : "5902018aedffcb32008b4567"
 */

@lockable
export default class Page extends Model {
  // Put all page properties here.
  // the placeholder object
  @observable content = '';

  @observable items = [];

  @observable meta = observable.map({});

  @observable templateData = observable.map({});

  @observable __gridOrder = [];

  @observable sub = [];

  @observable i18n = {};

  // the template id of this page
  @observable templateId = '';

  // whether or not this item is marked as active
  @observable isActive = false;

  @observable complete = false;

  @observable contentTemplatesLoaded = false;

  // whether a page has changed since last publish
  @observable hasChanged = false;

  @observable lastPublishedAt = null;

  // Last date/time this was published
  @observable lastUnpublishedAt = null;

  // Last date/time this was unpublished
  @observable publishAt = null;

  // A date/time when this will be published
  @observable publicationNotAllowed = false;

  // Whether publishing is locked for this page
  @observable publishedUrl = null;

  // The url/filename of this page after publishing
  @observable releasePublicationLockAt = null;

  // Date/time when the lock will be cleared
  @observable isGhostPage = false;

  @observable navigationArticleId = null;

  @observable isSaving = false;

  @observable redirectUrl = '';

  @observable redirectTarget = null;

  @observable reviewModelId = null;

  // 1-current tab 2-new tab
  @observable redirectType = 1; // 1-internal 2-external

  @observable unpublishAt = null; // A date/time when this will be unpublished

  @computed get isEmpty() {
    return !this.items.length
  }

  // special template type. renders without template
  @computed get isRedirectPage() {
    return this.templateId === -1
  }

  // with negative templateId, special rendering. no template needed
  @computed get needsTemplate() {
    return this.templateId < 0
  }

  @computed get channelShortcut() {
    const channelShortcut = deepGet(
      CircularDependencyFreeStore,
      'projectStore.current.channelShortcut'
    )
    return channelShortcut
  }

  @computed get asJSON() {
    return this.getJSON()
  }

  /** Warning: getJson is enhanced/changed by plugins to include data they need saved */
  getJSON() {
    return {
      id: this.id,
      name: this.name,
      navigationName: this.navigationName,
      navigationArticleId: this.navigationArticleId,
      i18n: toJS(this.i18n),
      content: toJS(this.templateData),
      parentId: this.parentId,
      projectId: this.projectId,
      // position: this.position, should not be used with a normal save
      hideNavigation: this.hideNavigation,
      createFolder: this.createFolder,
      templateId: this.templateId,
      publicationNotAllowed: this.publicationNotAllowed,
      releasePublicationLockAt: this.releasePublicationLockAt,
      // these props may be used upon page creation to create a page
      // with a template matching this spec. This is handy when the templateId
      // is not known
      vendor: this.vendor || null,
      shortcut: this.shortcut || null,
      hideInSearch: this.hideInSearch,
      type: this.type,
      meta: this.meta,
      updatedBy: this.updatedBy,
      redirectTarget: this.redirectTarget || 1, // default
      redirectType: this.redirectType || 1, // default
      redirectUrl: this.redirectUrl,
      hideBreadcrumb: this.hideBreadcrumb,
      hideFromRobots: this.hideFromRobots,
    }
  }

  /**
   * The page's navigation article. Templates may declare a grid position
   * as `navigationArticleSource` (see `__gridOrder`), then the article
   * lying there is the navigation article: the one already set as long as
   * it is still there, otherwise the first one found. Without such a
   * position the value is whatever was set on the page.
   * @return {Number|String|null} - The article id. `''` if such a
   * position exists but holds no article: the API clears the column on an
   * empty String, a `null` is ignored by its `isset()` check.
   */
  getNavigationArticleId() {
    const sources = (this.__gridOrder || [])
      .filter(entry => entry.navigationArticleSource)

    if (!sources.length) {
      return this.navigationArticleId
    }

    // navigationArticleId may be a String (or `''`, see below), item ids
    // are always Numbers.
    const currentId = this.navigationArticleId
      ? this.navigationArticleId * 1
      : null

    const articleIds = sources.reduce((memo, { gr, gb }) => {
      // gr/gb may be Numbers in __gridOrder, getGridBlock expects Strings.
      const block = this.getGridBlock({ gr: `${gr}`, gb: `${gb}` }).block || []

      return [
        ...memo,
        ...block
          .filter(item => item && item.type === 'article')
          .map(item => item.id * 1),
      ]
    }, [])

    if (!articleIds.length) {
      return ''
    }

    return articleIds.indexOf(currentId) > -1 ? currentId : articleIds[0]
  }

  // react-tree-component props
  @computed get children() {
    return this.sub
  }

  @computed get hasChildren() {
    return !!this.sub.length
  }

  getExpandedKeys() {
    let expandedKeys = []
    let curr = this.parent || this.store.getPartialById(this.parentId)
    while (curr) {
      expandedKeys.push(`${curr.id}`)
      curr = curr.parent || this.store.getPartialById(curr.parentId)
    }
    expandedKeys = expandedKeys.reverse()

    return expandedKeys
  }

  @computed get name() {
    return i18n(this, 'name', this.createdIso)
  }

  @computed get navigationName() {
    return i18n(this, 'navigationName', this.createdIso)
  }

  @computed get title() {
    return i18n(this, 'title', this.createdIso)
  }

  @computed get keywords() {
    return i18n(this, 'keywords', this.createdIso)
  }

  @computed get description() {
    return i18n(this, 'description', this.createdIso)
  }

  get leaf() {
    return false // !this.children.length
  }

  get collapsed() {
    return 'isCollapsed' in this && this.isCollapsed !== undefined
      ? this.isCollapsed
      : this.depth >= 1
  }

  set collapsed(isCollapsed) {
    this.isCollapsed = isCollapsed
  }

  get isDeletable() {
    // Do not allow to delete or archive this page if:
    // a) this is the Startpage
    if (
      this.parentId === PAGE_ID_STARTPAGE
      || (this.parent && this.parent.id === PAGE_ID_STARTPAGE)
    ) {
      return false
    }

    // b) one of the sub pages is published
    return this.publishedChildren.length === 0
  }

  get publishedChildren() {
    const publishedChildren = []
    this.traverse((page) => {
      if (page.publishedUrl) {
        publishedChildren.push(page)
      }
    })
    return publishedChildren
  }

  get allChildren() {
    const allChildren = []
    this.traverse((page) => {
      allChildren.push(page.id)
    })
    return allChildren
  }

  get updatedByName() {
    return getUserNameById(this.updatedBy)
  }

  constructor(store, rawData, opts = {}) {
    opts.ignore = true
    super(store, rawData, opts)
    this.isActive = false
    this.store = store

    this.autoSavable = false
    this.autoSave = false

    this.destoySaveHandler = null
    this.modelOpts = { idProp: 'id' }

    this.set(rawData)

    this.initLockable()

    this.isSpecialPage = this.ensureIsSpecialPage()
  }

  parse(data) {
    // eslint-disable-next-line no-underscore-dangle
    if (data.__parsed) {
      // if the data was already parsed once, bail out immediately
      return data
    }

    if (!data.id && data.data) {
      data = data.data
    }

    if (data.id) {
      data.id *= 1
    }

    if (data.projectId) {
      data.projectId *= 1
    }

    if (this.content && !data.content) {
      delete data.content
    }

    if (!data.meta || Array.isArray(data.meta)) {
      data.meta = {}
    }

    if (
      (!this.templateData && !data.templateData)
      || Array.isArray(data.templateData)
    ) {
      data.templateData = {}
    }

    if (!data.itemTemplates || Array.isArray(data.itemTemplates)) {
      data.itemTemplates = {}
    }

    if (
      !data.itemTemplates.article
      || Array.isArray(data.itemTemplates.article)
    ) {
      data.itemTemplates.article = {}
    }

    if (
      !data.itemTemplates.widget
      || Array.isArray(data.itemTemplates.widget)
    ) {
      data.itemTemplates.widget = {}
    }

    delete data.name

    // eslint-disable-next-line no-underscore-dangle
    data.__parsed = true

    return data
  }

  set(data) {
    data = this.parse(toJS(data))

    // If a value is null it could still exist
    // but the backend did not send it.
    let templateData = null
    if ('templateData' in data) {
      templateData = data.templateData
      delete data.templateData
    }

    let items = null
    if ('items' in data) {
      items = data.items
      delete data.items
    }

    // Use transaction to ensure assigning all values before mobx starts reacting
    transaction(() => {
      Object.assign(this, data)
    })

    if (items) {
      if (items.length && this.items && this.items.length) {
        this.updateItems(items)
      }
      else {
        this.items = items
      }
    }

    if (templateData) {
      this.updateTemplateData(templateData)
    }

    // subs are not delivered when requesting a single page, so
    // in case there is no data for data.sub, don't do anything,
    // unless the model has no sub property yet
    let subs = null

    if (!this.sub || data.sub) {
      const childDepth = data.depth + 1
      subs = (data.sub || []).map((item) => {
        item.depth = childDepth
        const subModel = new Page(this.store, item)
        subModel.parent = this
        subModel.parentId = this.id
        return subModel
      })
      delete data.sub
    }

    if (subs) {
      // somehow mobx replace is buggy, splice seems to work
      // this.sub.replace(subs)
      if (!this.sub) {
        this.sub = []
      }
      this.sub.splice(0, this.sub.length, ...subs)
    }

    // if the page was complete once, it cannot become incompete again
    if (!this.complete) {
      this.complete = !!(data.content && this.items)
    }
  }

  ensureIsSpecialPage() {
    return !!specialPageIds.find(id => id === this.id)
  }

  updateTemplateData(newTemplateData) {
    if (!this.templateData) {
      this.templateData = observable.map({})
    }

    // properly create the content observable map
    const grs = Object.keys(newTemplateData)
    grs.forEach((gr) => {
      if (!this.templateData.has(gr)) {
        this.templateData.set(gr, observable.map({}))
      }

      const gridRow = this.templateData.get(gr)

      const gbs = Object.keys(newTemplateData[gr])
      gbs.forEach((gb) => {
        if (!gridRow.has(gb)) {
          gridRow.set(gb, observable([]))
        }

        const gridBlock = gridRow.get(gb)
        // if (!isObservableMap(gridBlock)) {
        gridBlock.splice(
          0,
          gridBlock.length,
          ...(newTemplateData[gr][gb] || [])
        )
        // }
        // else {
        //   gridBlock.merge(newTemplateData[gr][gb])
        // }
      })
    })
  }

  updateItems(items) {
    const command = new UpdatePageContentCommand(this, items).exec()
  }

  hasItem(id) {
    return !!this.items.find(item => item.id === id)
  }

  // TODO the following lines should almost all wander into the GridBlock

  ensureGridRowId(id) {
    return id.indexOf('gr') === 0 ? id : `gr${id}`
  }

  ensureGridBlockId(id) {
    return id.indexOf('gb') === 0 ? id : `gb${id}`
  }

  getGridRow(gridRowId, createIfNotExist = false) {
    gridRowId = this.ensureGridRowId(gridRowId)
    let row = this.templateData.get(gridRowId)
    if (!row && createIfNotExist) {
      row = observable.map({})
      this.templateData.set(gridRowId, row)
    }
    return row
  }

  getGridBlock(position, createIfNotExist = false) {
    const row = this.getGridRow(position.gr, createIfNotExist)
    let block = undefined
    const gridBlockId = this.ensureGridBlockId(position.gb)

    if (row) {
      block = row.get(gridBlockId)
      if (!block && createIfNotExist) {
        block = observable([])
        row.set(gridBlockId, block)
      }
    }

    if (position.content && position.content.length) {
      let item = block[position.i || position.index]
      if (!item) {
        item = block[position.i || position.index] = []
      }
      position.content.forEach((index) => {
        if (!item[index]) {
          item[index] = []
        }
        item = item[index]
      })
    }

    return new GridBlock(block, position)
  }

  // --- END ---

  /** Get the translated name for that page
   * @returns {String} name - The name of the Page.
   * The fake Pages for the Navigation will have there names translated.
   */
  getName() {
    return this.isSpecialPage && typeof formatMessage === 'function'
      ? formatMessage({ id: this.name })
      : this.name
  }

  setName(name) {
    i18n(this, 'name', this.createdIso, name)
    this.save()
  }

  save() {
    // Assigned to the model, not just added to the payload in getJSON():
    // plugins wrap getJSON() and read `navigationArticleId` off the model
    // (e.g. to mirror it into `meta`), so the field has to be up to date
    // before the payload is built.
    this.navigationArticleId = this.getNavigationArticleId()

    if (this.transactionsRunning) {
      this.saveCalled = true
      return Promise.resolve(this)
    }
    return this.store.save(this.id, this)
  }

  saveChild(data) {
    if (this.transactionsRunning) {
      this.saveCalled = true
      return Promise.resolve(this)
    }
    return this.store.saveChild(data, this)
  }

  /**
   * Inserts a new item of given type at the given position.
   * @param {Object} item - The item to be added.
   * @param {Number} item.id - The id of the item to be added.
   * @param {String} item.type - The type of the item to be added.
   * @param {String} type - The type of the item to be added.
   * @param {String} position - The position where to insert the
   * item.
   * @param {String|Number} position.gr - The grid row.
   * @param {String|Number} position.gb - The grid block.
   * @param {Number} position.i - [deprecated] The index within
   * the grid block.
   * @param {Number} position.index - The index within the grid
   * block.
   * @param {Array<Number>} position.content - The deeper path
   * usually set by Widgets. E.g. [1, 5] in an accordion widget
   * may mean: In the first accordion area at position 5.
   */
  @action
  insertContent(item, type, position) {
    const command = new InsertIntoPageCommand(this, {
      item,
      type,
      position,
    })
    return command.exec()
  }

  /**
   * Removes the item at the given position.
   * @todo send the item along to verify the item to be
   * removed is actually the one at the given position.
   * @param {String} position - The position where to insert the
   * item.
   * @param {String|Number} position.gr - The grid row.
   * @param {String|Number} position.gb - The grid block.
   * @param {Number} position.i - [deprecated] The index within
   * the grid block.
   * @param {Number} position.index - The index within the grid
   * block.
   * @param {Array<Number>} position.content - The deeper path
   * usually set by Widgets. E.g. [1, 5] in an accordion widget
   * may mean: In the first accordion area at position 5.
   */
  @action
  removeContent(position) {
    const command = new RemoveFromPageCommand({
      pageModel: this,
      data: position,
    })
    return command.exec()
  }

  /**
   * Moves an article from position `fromPosition` to position `toPosition.`
   * @param {Object} fromPosition - The position to move the object from.
   * @param {Object} toPosition - The position to move the object to.
   */
  @action
  moveContent(fromPosition, toPosition) {
    const command = new MoveInPageCommand(this, fromPosition, toPosition)
    return command.exec()
  }

  /**
   *  Get the grid position for given content item within the grid of the page.
   *  @param {Object} item - The item to look for
   *  @param {Number} item.id - The id of the item.
   *  @param {String} item.contentType - The type of the item.
   */
  getContentPosition(item) {
    let position = null
    this.traverseGrid((gr, gb, i, itemAtPos) => {
      if (
        item.id * 1 === itemAtPos.id * 1
        && (item.type || item.contentType)
          === (itemAtPos.type || itemAtPos.contentType)
      ) {
        position = { gr, gb, i }
      }
    })
    return position
  }

  /**
   * @param {Page} page
   * @param {number} index
   * @param {Object} opts
   *
   */
  addChild(page, index, opts = {}) {
    if (!(page instanceof Page)) {
      throw new Error('Only page instances can be added as sub pages')
    }

    if (
      (page.parent && page.parent !== this)
      || (page.parentId && page.parentId !== this.id)
    ) {
      // eslint-disable-next-line max-len
      throw new Error(
        `The page ${page.name} (${page.id}) is already child of ${page.parent.name} (${page.parent.id}). Remove it first to be able to add it.`
      )
    }

    const currPageIndex = this.indexOfChild(page)

    const isAlreadyChild = currPageIndex >= 0

    if (isAlreadyChild && !opts.ignoreExist) {
      // eslint-disable-next-line max-len
      throw new Error(
        `The page ${page.name} (id: ${page.id}) already exists as a child (name: ${this.sub[currPageIndex].name}) in the collection of the page ${this.name} (id: ${this.id}).`
      )
    }

    if (!this.sub) {
      this.sub = []
    }

    if (isAlreadyChild) {
      // extract the child at the old pos
      this.sub.splice(currPageIndex, 1)
    }

    if (index === undefined) {
      // always add new pages at the top
      index = 0
    }
    else if (index > this.sub.length) {
      index = this.sub.length
    }

    page.parent = this
    page.parentId = this.id
    page.position = index + 1 // backend starts counting at 1
    page.depth = this.depth + 1

    this.sub.splice(index, 0, page)
  }

  /**
   *
   * @param {Page} page
   */
  removeChild(page) {
    const index = this.indexOfChild(page)
    page.parent = null
    page.parentId = null
    page.position = -1
    page.depth = -1
    this.sub.splice(index, 1)
  }

  /**
   *
   * @param {Page} page
   */
  indexOfChild(page) {
    let index = this.sub.indexOf(this.ensurePage(page))
    if (index >= 0) {
      return index
    }

    page = this.sub.find(p => p.id === page.id)
    if (page) {
      index = this.sub.indexOf(this.ensurePage(page))
    }

    return index
  }

  /**
   *
   * @param {Page} page
   */
  ensurePage(page) {
    let pageId

    if (typeof page === 'string' || typeof page === 'number') {
      pageId = page
      page = null
    }

    if (pageId) {
      page = this.sub.find(sub => sub.id === pageId)
    }

    if (!page) {
      throw new Error('Cannot remove page. Page not found in children!')
    }

    return page
  }

  traverse(fn) {
    fn(this)
    this.sub.map(page => page.traverse(fn))
  }

  traverseGrid(fn) {
    const data = toJS(this.templateData)
    Object.keys(data)
      .forEach(gr => Object.keys(data[gr])
        .forEach(gb => data[gr][gb]
          .forEach((item, i) => fn(gr, gb, i, item))
        )
      )
  }

  /**
   * Starts a transaction. All code calling save after the
   * transaction was started will be blocked untill
   * PageModel#endTransaction was called.
   * Multiple calls to this function while a transaction is
   * already running will raise a counter for running
   * transactions. Every call to PageModel#endTransaction
   * decreases that counter again.
   * Cals to PageModel#save() while a transaction is running
   * will **always** return a successfully resolving Promise!l
   * @method startTransaction
   */
  startTransaction() {
    if (!this.transactionsRunning) {
      this.saveCalled = false
      if (!this.transactionsRunning) {
        this.transactionsRunning = 0
      }
      this.transactionsRunning += 1
    }
  }

  /**
   * Ends a transaction. If this was the last transaction on the
   * transaction stack and PageModel#save would have been called
   * some time time a transaction was active, save will be called
   * now.
   * @method endTransaction
   * @return {Promise<Object>|null} - The promise of the call to
   * save, or null if save was actually never called while the
   * transaction was active.
   */
  endTransaction() {
    this.transactionsRunning -= 1
    if (!this.transactionsRunning && this.saveCalled) {
      this.saveCalled = false
      return this.save()
    }
    return null
  }
}
